import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery as useZeroQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { useState } from "react";
import { toast } from "sonner";
import { toFriendlyMutationError } from "@/lib/error-utils";
import { generateUUID } from "@/lib/utils";

export type TaskState = "backlog" | "todo" | "in_progress" | "done";

export interface Task {
  id: string;
  title: string;
  description: string | null;
  state: TaskState;
  due_date: string | null;
  created_at: string;
  updated_at: string;
}

export type TaskInsert = {
  id?: string;
  title: string;
  description?: string | null;
  state?: TaskState;
  due_date?: string | null;
};

export type TaskUpdate = {
  id?: string;
  title?: string;
  description?: string | null;
  state?: TaskState;
  due_date?: string | null;
};

export interface TaskWithRelations extends Task {
  assignees: Array<{
    id: string;
    person: {
      id: string;
      first_name: string;
      last_name: string;
    };
  }>;
  projects: Array<{
    id: string;
    project: {
      id: string;
      name: string;
      number: string | null;
      event_start_date: string | null;
    };
  }>;
  project_groups: Array<{
    id: string;
    project_group: {
      id: string;
      name: string;
      number: string | null;
    };
  }>;
}

type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };
type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};
type MutationOptions<T> = { onSuccess?: (data: T) => void };

type ZeroTaskRow = {
  id: string;
  title: string;
  description?: string | null;
  state: TaskState;
  dueDate?: number | string | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
  assignees?: Array<{
    id: string;
    person?: {
      id: string;
      firstName: string;
      lastName: string;
    } | null;
  }> | null;
  projects?: Array<{
    id: string;
    project?: {
      id: string;
      name: string;
      number?: string | null;
      eventStartDate?: number | string | null;
    } | null;
  }> | null;
  projectGroups?: Array<{
    id: string;
    projectGroup?: {
      id: string;
      name: string;
      number?: string | null;
    } | null;
  }> | null;
};

function toLegacyTimestamp(value: number | string | null | undefined) {
  if (typeof value === "number") return new Date(value).toISOString();
  return value ?? "";
}

function toLegacyDate(value: number | string | null | undefined) {
  if (typeof value === "number")
    return new Date(value).toISOString().slice(0, 10);
  return value ?? null;
}

function toLegacyTask(row: ZeroTaskRow): TaskWithRelations {
  return {
    id: row.id,
    title: row.title,
    description: row.description ?? null,
    state: row.state,
    due_date: toLegacyDate(row.dueDate),
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
    assignees: (row.assignees ?? [])
      .filter((assignee) => !!assignee.person)
      .map((assignee) => ({
        id: assignee.id,
        person: {
          id: assignee.person!.id,
          first_name: assignee.person!.firstName,
          last_name: assignee.person!.lastName,
        },
      })),
    projects: (row.projects ?? [])
      .filter((project) => !!project.project)
      .map((project) => ({
        id: project.id,
        project: {
          id: project.project!.id,
          name: project.project!.name,
          number: project.project!.number ?? null,
          event_start_date: toLegacyDate(project.project!.eventStartDate),
        },
      })),
    project_groups: (row.projectGroups ?? [])
      .filter((projectGroup) => !!projectGroup.projectGroup)
      .map((projectGroup) => ({
        id: projectGroup.id,
        project_group: {
          id: projectGroup.projectGroup!.id,
          name: projectGroup.projectGroup!.name,
          number: projectGroup.projectGroup!.number ?? null,
        },
      })),
  };
}

async function reportZeroMutationFailure(
  serverResult: Promise<ZeroMutationResult>,
  fallback: string,
) {
  const result = await serverResult;
  if (result.type === "error") {
    throw new Error(result.error?.message || fallback);
  }
}

function useZeroMutation<TInput, TResult>({
  run,
  onError,
}: {
  run: (input: TInput) => Promise<TResult>;
  onError: string;
}) {
  const [isPending, setIsPending] = useState(false);

  const wrappedRun = async (input: TInput) => {
    setIsPending(true);
    try {
      return await run(input);
    } catch (error) {
      throw toFriendlyMutationError(error);
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (input: TInput, options?: MutationOptions<TResult>) => {
      void wrappedRun(input)
        .then((data) => options?.onSuccess?.(data))
        .catch((error) => toast.error(`${onError}: ${error.message}`));
    },
    mutateAsync: wrappedRun,
    isPending,
  };
}

export function useTasks() {
  const [data, details] = useZeroQuery(appQueries.tasks.list());
  const tasks = ((data ?? []) as ZeroTaskRow[]).map(toLegacyTask);

  return {
    data: tasks,
    isLoading: details.type !== "complete" && tasks.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useTask(id: string | undefined) {
  const query = id ? appQueries.tasks.byID({ id }) : undefined;
  const [data, details] = useZeroQuery(query, { enabled: !!id });
  const task = data ? toLegacyTask(data as ZeroTaskRow) : null;

  return {
    data: task,
    isLoading: !!id && details.type !== "complete" && !task,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useCreateTask() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<TaskInsert, TaskWithRelations>({
    onError: "Failed to create task",
    run: async (task) => {
      const id = task.id ?? generateUUID();
      const input = {
        id,
        title: task.title,
        description: task.description ?? null,
        state: task.state ?? "backlog",
        dueDate: task.due_date ?? null,
      };
      const mutation = zero.mutate(appMutators.tasks.create(input));
      await mutation.client;
      await reportZeroMutationFailure(mutation.server, "Task create rejected");
      toast.success("Task created");
      return toLegacyTask({
        ...input,
        createdAt: "",
        updatedAt: "",
        assignees: [],
        projects: [],
        projectGroups: [],
      });
    },
  });
}

export function useUpdateTask() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<
    TaskUpdate & { id: string },
    TaskUpdate & { id: string }
  >({
    onError: "Failed to update task",
    run: async ({ due_date, ...updates }) => {
      const input = {
        id: updates.id,
        title: updates.title,
        description: updates.description,
        state: updates.state,
        dueDate: due_date,
      };
      const mutation = zero.mutate(appMutators.tasks.update(input));
      await mutation.client;
      await reportZeroMutationFailure(mutation.server, "Task update rejected");
      return { ...updates, due_date };
    },
  });
}

export function useDeleteTask() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<string, string>({
    onError: "Failed to delete task",
    run: async (id) => {
      const mutation = zero.mutate(appMutators.tasks.delete({ id }));
      await mutation.client;
      await reportZeroMutationFailure(mutation.server, "Task delete rejected");
      toast.success("Task deleted");
      return id;
    },
  });
}

export function useAddTaskAssignee() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<{ taskId: string; personId: string }, void>({
    onError: "Failed to add assignee",
    run: async ({ taskId, personId }) => {
      const mutation = zero.mutate(
        appMutators.taskAssignees.add({
          id: generateUUID(),
          taskID: taskId,
          personID: personId,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Task assignee add rejected",
      );
    },
  });
}

export function useRemoveTaskAssignee() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<{ taskId: string; personId: string }, void>({
    onError: "Failed to remove assignee",
    run: async ({ taskId, personId }) => {
      const mutation = zero.mutate(
        appMutators.taskAssignees.remove({
          id: generateUUID(),
          taskID: taskId,
          personID: personId,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Task assignee remove rejected",
      );
    },
  });
}

export function useAddTaskProject() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<{ taskId: string; projectId: string }, void>({
    onError: "Failed to add project",
    run: async ({ taskId, projectId }) => {
      const mutation = zero.mutate(
        appMutators.taskProjects.add({
          id: generateUUID(),
          taskID: taskId,
          projectID: projectId,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Task project add rejected",
      );
    },
  });
}

export function useRemoveTaskProject() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<{ taskId: string; projectId: string }, void>({
    onError: "Failed to remove project",
    run: async ({ taskId, projectId }) => {
      const mutation = zero.mutate(
        appMutators.taskProjects.remove({
          id: generateUUID(),
          taskID: taskId,
          projectID: projectId,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Task project remove rejected",
      );
    },
  });
}

export function useAddTaskProjectGroup() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<{ taskId: string; projectGroupId: string }, void>({
    onError: "Failed to add project group",
    run: async ({ taskId, projectGroupId }) => {
      const mutation = zero.mutate(
        appMutators.taskProjectGroups.add({
          id: generateUUID(),
          taskID: taskId,
          projectGroupID: projectGroupId,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Task project group add rejected",
      );
    },
  });
}

export function useRemoveTaskProjectGroup() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<{ taskId: string; projectGroupId: string }, void>({
    onError: "Failed to remove project group",
    run: async ({ taskId, projectGroupId }) => {
      const mutation = zero.mutate(
        appMutators.taskProjectGroups.remove({
          id: generateUUID(),
          taskID: taskId,
          projectGroupID: projectGroupId,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Task project group remove rejected",
      );
    },
  });
}
